import React, {
  type Dispatch,
  type FC,
  type SetStateAction,
  useState,
} from 'react';
import {
  Popover as PopoverWrapper,
  Text,
  TextButton,
} from '@wix/design-system';
import type { ClonePrompt } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import css from './TerminalExample.scss';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common';
import { DataHooks } from '../../../../../constants/dataHooks';
import classNames from 'classnames';
import { getDisplayPackageManager } from '../../../../../utils/getDisplayPackageManager';
import { useTemplatesPageBi } from '../../../../../hooks/useTemplatesPageBi';
import { useTemplatesData } from '../../../../../hooks/useTemplatesData';

type PopoverMenuProps = {
  clonePromptControl: {
    selectedClonePrompt: ClonePrompt;
    setSelectedClonePrompt: Dispatch<SetStateAction<ClonePrompt>>;
  };
};

type MenuItemProps = {
  clonePrompt: ClonePrompt;
  onClick: (clonePrompt: ClonePrompt) => void;
  isSelected: ClonePrompt;
};
export const PackageManagerPopoverMenu: FC<PopoverMenuProps> = ({
  clonePromptControl,
}) => {
  const { currentTemplate: template } = useTemplatesData();
  const clonePrompts = template.clonePrompt;
  const [popoverMenuOpened, setPopoverMenuOpened] = useState(false);
  const { selectedClonePrompt, setSelectedClonePrompt } = clonePromptControl;
  const { reportPackageManagerSelected } = useTemplatesPageBi();

  const onClickItem = (clonePrompt: ClonePrompt) => {
    reportPackageManagerSelected(template.id, clonePrompt.packageManager);
    setSelectedClonePrompt(clonePrompt);
    setPopoverMenuOpened(false);
  };

  return (
    <PopoverWrapper
      dataHook={DataHooks.PackageManagerPopover}
      onClickOutside={() => setPopoverMenuOpened(false)}
      zIndex={10000}
      shown={popoverMenuOpened}
      appendTo="window"
      placement="bottom"
      theme="dark"
      animate
      hideDelay={25}
      moveBy={{ y: 5 }}
    >
      <PopoverWrapper.Element>
        <TextButton
          dataHook={DataHooks.PackageManagerButton}
          className={css.jetBrainsMono}
          skin="light"
          size="small"
          onClick={() => setPopoverMenuOpened(!popoverMenuOpened)}
          suffixIcon={popoverMenuOpened ? <ChevronUp /> : <ChevronDown />}
        >
          {getDisplayPackageManager(selectedClonePrompt)}
        </TextButton>
      </PopoverWrapper.Element>
      <PopoverWrapper.Content>
        <div className={css.popoverMenuLayout}>
          {clonePrompts?.map((clonePrompt) => (
            <MenuItem
              key={clonePrompt.packageManager}
              clonePrompt={clonePrompt}
              onClick={onClickItem}
              isSelected={selectedClonePrompt}
            />
          ))}
        </div>
      </PopoverWrapper.Content>
    </PopoverWrapper>
  );
};

const MenuItem: FC<MenuItemProps> = ({ clonePrompt, onClick, isSelected }) => {
  return (
    <div
      onClick={() => onClick(clonePrompt)}
      className={classNames(css.popoverMenuItem, {
        [css.isSelectedItem]: isSelected === clonePrompt,
      })}
    >
      <Text className={css.jetBrainsMono} light size="small" weight="thin">
        {getDisplayPackageManager(clonePrompt)}
      </Text>
    </div>
  );
};
